import React, { useState, useRef } from "react";
import styled from "styled-components";
import {
  UI_BODY_SEMIBOLD_SM_DF,
  UI_BODY_SM_DF,
  UI_CAPTION,
  UI_TAG,
  colors,
  icons,
  colors2,
  OKELink,
  UI_CAPTION_DF,
  Wrapper700,
} from "oolib";
import { genIcon } from "oolib/dist/v2/components/Dropdowns/utils/genIcon";
import { useQuery } from "react-query";
import axios from "axios";
import { useGetQueryData } from "@/utils/react-query-hooks/general";
import { RotatingCaret } from "@/components/generalUI/RotatingCaret";
const {Sparkle} = icons;


// Outer wrapper with padding
const OuterWrapper = styled.div`
  /* padding: 1.5rem; */
`;

// Main container with card styling similar to AI Chat messages
const DocumentSummaryCard = styled(Wrapper700)`
  /* border: 1px solid ${colors2.grey10}; */
  
  border-radius: 0.6rem;
  background-color: #ffffff;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border: 1px solid ${colors2.grey10};
  border-radius: 8px;
  padding: 14px;
`;

// Header with icon and title
const HeaderContainer = styled.div`
  cursor: pointer;
  user-select: none;
`;

const HeaderSection = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  justify-content: space-between;
`;


// Scrollable content area
const ContentArea = styled.div`
  max-height: ${({ isExpanded }) => isExpanded ? "400px" : "none"};
  overflow-y: ${({ isExpanded }) => isExpanded ? "auto" : "visible"};
  margin-top: 8px;
`;

const ContentWrapper = styled.div`
  /* max-width: 700px; */
`;

// Sticky footer for toggle button
const FooterSection = styled.div`
  margin-top: 4px;
  background-color: #ffffff;
`;

// Section styling
const SectionContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
`;

const SectionHeader = styled(UI_BODY_SEMIBOLD_SM_DF)`
  color: ${colors2.grey70};
  /* margin-bottom: 0.1rem; */
`;

const SectionContent = styled(UI_BODY_SM_DF)`
  color: inherit;
  line-height: 1.5;
`;

const ActorCard = styled.div`
  background: ${colors2.grey2};
  padding: 0.8rem;
  border-radius: 0.4rem;
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  /* Subtle left-accent for actors classified by the summarizer as "customer"
     (roleForAnnotation === "customer"). Lets a reviewer scan a Key Actors
     list and spot whether the summarizer's classification looks right —
     without adding text noise. Non-customer cards get no accent. */
  ${({ $customer }) =>
    $customer
      ? `border-left: 2px solid ${colors2.grey60}; padding-left: calc(0.8rem - 2px);`
      : ""}
`;

const ActorName = styled(UI_BODY_SEMIBOLD_SM_DF)`
  color: inherit;
`;

const ActorRole = styled(UI_CAPTION)`
  color: ${colors2.grey70};
`;

const ActorDescription = styled(UI_BODY_SM_DF)`
  color: inherit;
`;

const KeyPointCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  padding-left: 1rem;
  border-left: 2px solid ${colors2.grey10};
`;

const Evidence = styled(UI_CAPTION)`
  color: ${colors2.grey70};
  font-style: italic;
`;

const ListItem = styled.li`
  margin-bottom: 0.4rem;
  list-style-type: disc;
`;

const SectionsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.6rem;
`;

const ActorsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 0.8rem;
`;

const KeyPointsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
`;

const ShortSummaryText = styled(UI_BODY_SM_DF)`
  color: inherit;
  line-height: 1.6;
`;

// Fetch hook for document summary.
// When the extracted text is below the configured minTextLength guardrail,
// the BE returns 200 with a summary object carrying `notSummarized: true`
// (and the raw text in shortSummary/context). Consumers check that flag to
// render the "too short" message.
const useGetDocumentSummary = ({ documentId, contentType, enabled = true }) => {
  return useQuery(
    ["documentSummary", documentId],
    () =>
      axios.get(`/api/content/getDocumentSummary`, {
        params: { documentId, contentType },
      }),
    {
      enabled: enabled && !!documentId,
      staleTime: 1000 * 60 * 5, // 5 minutes
      refetchOnWindowFocus: false,
    }
  );
};

/**
 * DocumentSummary Component
 *
 * Displays a structured document summary with sections:
 * - Short Summary (collapsed view)
 * - Actors/Stakeholders
 * - Context
 * - Core Problem
 * - Key Points (with evidence)
 * - Insights
 * - Implications
 *
 * @param {Object} props
 * @param {string} props.documentId - The document's _id
 * @param {string} props.contentType - Content type of the document
 */
const DocumentSummary = ({ documentId, contentType }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  // Lazy gate: card lands collapsed and the API call only fires after the user
  // expands it for the first time. Mirrors the `hasTriggered` pattern used by
  // AnnoExplorer's SummaryV2.
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [hasTriggered, setHasTriggered] = useState(false);
  const contentAreaRef = useRef(null);

  const handleExpand = () => {
    setIsExpanded(true);
    // Scroll the content area to the top after expanding
    setTimeout(() => {
      if (contentAreaRef.current) {
        contentAreaRef.current.scrollTop = 0;
      }
    }, 50);
  };

  const handleHeaderClick = () => {
    if (!hasTriggered) {
      setHasTriggered(true);
      setIsCollapsed(false);
      return;
    }
    setIsCollapsed(!isCollapsed);
  };

  const platformConfigs = useGetQueryData("platformConfigs");
  const documentSummaryConfig = platformConfigs?.ai?.aiSummarization?.documentSummary;

  // Check if feature is enabled via platformConfig
  const isEnabled = documentSummaryConfig?.enable;

  // Hook must be called unconditionally (before any early returns)
  const { data, status } = useGetDocumentSummary({
    documentId,
    contentType,
    enabled: isEnabled && hasTriggered,
  });

  const isLoading = status === "loading";

  const genHeaderSection = () => (
    <HeaderContainer isCollapsed={isCollapsed} onClick={isLoading ? undefined : handleHeaderClick} style={isLoading ? { cursor: 'default' } : {}}>
      <HeaderSection>
        <div style={{display: "flex", alignItems: "center", gap: "0.6rem"}}>
        <Sparkle size={16} weight="bold" />
        <UI_BODY_SEMIBOLD_SM_DF>{isLoading ? 'Summarizing document...' : 'Document Summary'}</UI_BODY_SEMIBOLD_SM_DF>
        </div>
        {!isLoading && <RotatingCaret iconSize={20} color={colors2.black} position={isCollapsed ? "down" : "up"} />}
      </HeaderSection>
    </HeaderContainer>
  )

  // Early returns after hooks
  if (!isEnabled) {
    return null;
  }

  // Untriggered: render the clickable header only. Click latches hasTriggered
  // and fires the API call.
  if (!hasTriggered) {
    return (
      <DocumentSummaryCard>
        {genHeaderSection()}
      </DocumentSummaryCard>
    );
  }

  if (isLoading) {
    return (
        <DocumentSummaryCard>
          {genHeaderSection()}
        </DocumentSummaryCard>
    );
  }

  // Guardrail message: BE marked this summary as not-summarized (text below
  // MIN_TEXT_LENGTH). The summary object still carries the raw text, but we
  // render an explicit message instead.
  if (data?.data?.summary?.notSummarized) {
    return (
      <DocumentSummaryCard>
        {genHeaderSection()}
        {!isCollapsed && (
          <ContentArea isExpanded={false}>
            <ContentWrapper>
              <ShortSummaryText style={{ color: colors2.grey60 }}>
                Text too short to summarize.
              </ShortSummaryText>
            </ContentWrapper>
          </ContentArea>
        )}
      </DocumentSummaryCard>
    );
  }

  if (status === "error" || !data?.data?.summary) {
    // Silently fail - don't show anything if no summary exists
    return null;
  }

  const summary = data.data.summary;

  // Check if summary has any content
  const hasContent =
    summary.shortSummary ||
    summary.actors?.length > 0 ||
    summary.context ||
    summary.coreProblem ||
    summary.keyPoints?.length > 0 ||
    summary.insights?.length > 0 ||
    summary.implications?.length > 0;

  if (!hasContent) {
    return null;
  }

  // Check if there's detailed content to expand to
  const hasDetailedContent =
    summary.actors?.length > 0 ||
    summary.context ||
    summary.coreProblem ||
    summary.keyPoints?.length > 0 ||
    summary.insights?.length > 0 ||
    summary.implications?.length > 0;

  // Collapsed view - show only shortSummary
  if (!isExpanded) {
    return (
        <DocumentSummaryCard>
          {genHeaderSection()}
          {!isCollapsed && (
            <>
              <ContentArea isExpanded={false}>
                <ContentWrapper>
                  {summary.shortSummary && (
                    <ShortSummaryText>{summary.shortSummary}</ShortSummaryText>
                  )}
                </ContentWrapper>
              </ContentArea>
              {hasDetailedContent && (
                <FooterSection>
                  
                    <div>
                      <OKELink onClick={handleExpand}>
                        <UI_BODY_SM_DF>
                          View detailed summary
                        </UI_BODY_SM_DF>
                      </OKELink>
                    </div>
                 
                </FooterSection>
              )}
            </>
          )}
        </DocumentSummaryCard>
    );
  }

  // Expanded view - show full detailed summary
  return (
      <DocumentSummaryCard>
        {genHeaderSection()}
        {!isCollapsed && (
          <>
            <ContentArea isExpanded={true} ref={contentAreaRef}>
              <ContentWrapper>
                <SectionsWrapper>
                  {/* Context Section */}
                  {summary.context && (
                    <SectionContainer>
                      <SectionHeader>Context</SectionHeader>
                      <SectionContent>{summary.context}</SectionContent>
                    </SectionContainer>
                  )}

                  {/* Core Problem Section */}
                  {summary.coreProblem && (
                    <SectionContainer>
                      <SectionHeader>Core Theme</SectionHeader>
                      <SectionContent>{summary.coreProblem}</SectionContent>
                    </SectionContainer>
                  )}

                  {/* Actors Section */}
                  {summary.actors?.length > 0 && (
                    <SectionContainer>
                      <SectionHeader>Key Actors</SectionHeader>
                      <ActorsGrid>
                        {summary.actors.map((actor, index) => (
                          <ActorCard
                            key={index}
                            $customer={actor.roleForAnnotation === "customer"}
                          >
                            <ActorName>{actor.name}</ActorName>
                            {actor.role && <ActorRole>{actor.role}</ActorRole>}
                            {actor.description && (
                              <ActorDescription>{actor.description}</ActorDescription>
                            )}
                          </ActorCard>
                        ))}
                      </ActorsGrid>
                    </SectionContainer>
                  )}

                  {/* Key Points Section */}
                  {summary.keyPoints?.length > 0 && (
                    <SectionContainer>
                      <SectionHeader>Key Points</SectionHeader>
                      <KeyPointsWrapper>
                        {summary.keyPoints.map((kp, index) => (
                          <KeyPointCard key={index}>
                            <SectionContent>{kp.point}</SectionContent>
                            {kp.evidence && <Evidence>"{kp.evidence}"</Evidence>}
                          </KeyPointCard>
                        ))}
                      </KeyPointsWrapper>
                    </SectionContainer>
                  )}

                  {/* Insights Section */}
                  {summary.insights?.length > 0 && (
                    <SectionContainer>
                      <SectionHeader>Insights</SectionHeader>
                      <ul style={{ margin: 0, paddingLeft: "1.2rem" }}>
                        {summary.insights.map((insight, index) => (
                          <ListItem key={index}>
                            <SectionContent>{insight}</SectionContent>
                          </ListItem>
                        ))}
                      </ul>
                    </SectionContainer>
                  )}

                  {/* Implications Section */}
                  {summary.implications?.length > 0 && (
                    <SectionContainer>
                      <SectionHeader>Implications</SectionHeader>
                      <ul style={{ margin: 0, paddingLeft: "1.2rem" }}>
                        {summary.implications.map((implication, index) => (
                          <ListItem key={index}>
                            <SectionContent>{implication}</SectionContent>
                          </ListItem>
                        ))}
                      </ul>
                    </SectionContainer>
                  )}
                </SectionsWrapper>
              </ContentWrapper>
            </ContentArea>
            <FooterSection>
           
                <OKELink onClick={() => setIsExpanded(false)}>
                  <UI_BODY_SM_DF>
                    View short summary
                  </UI_BODY_SM_DF>
                </OKELink>
           
            </FooterSection>
          </>
        )}
      </DocumentSummaryCard>
  );
};

export { DocumentSummary, useGetDocumentSummary };
